import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { SegmentedControl } from '../SegmentedControl/SegmentedControl';
import type { CollectCommentsMode } from '../Forms/types';
import { COLLECT_COMMENTS_MAX_PERCENT, COLLECT_COMMENTS_MIN_VALUE } from '../../utils/collectComments';
import styles from './CollectCommentsSubgroup.module.scss';

type ValueMode = Exclude<CollectCommentsMode, 'ALL'>;

const VALUE_FIELDS: Record<ValueMode, { name: string; max?: number }> = {
  NUMBER: { name: 'collectCommentsPerPostLimit' },
  PERCENT: { name: 'collectCommentsSamplePercent', max: COLLECT_COMMENTS_MAX_PERCENT },
};

const LABEL_ID = 'collectCommentsPerPostLabel';
const HINT_ID = 'collectCommentsPerPostHint';
const ERROR_ID = 'collectCommentsPerPostError';

const clampToRange = (value: number, min: number, max?: number) => {
  let next = Math.round(value);
  if (next < min) next = min;
  if (max !== undefined && next > max) next = max;
  return next;
};

interface CollectCommentsSubgroupProps {
  form: any;
  isDisabled?: boolean;
}

/**
 * "Comments per post" settings shown under the "Collect comments" checkbox
 * (Figma nodes 2005:2208 / 2005:1882 / 2005:1898 / 2005:1914).
 * Each mode keeps its own value field, so switching modes and back restores
 * what the user entered. The required check lives in NarrativeForm.validateForm.
 */
export const CollectCommentsSubgroup: React.FC<CollectCommentsSubgroupProps> = ({
  form,
  isDisabled = false,
}) => {
  const { t } = useTranslation();

  const modeOptions: { value: CollectCommentsMode; label: string }[] = [
    { value: 'ALL', label: t('All') },
    { value: 'NUMBER', label: t('Number') },
    { value: 'PERCENT', label: t('Percent') },
  ];

  const hints: Record<CollectCommentsMode, string> = {
    ALL: t('All comments to each post will be collected'),
    NUMBER: t('Maximum number of comments to each post'),
    PERCENT: t('Share of comments to each post'),
  };

  const placeholders: Record<ValueMode, string> = {
    NUMBER: t('e.g. 100'),
    PERCENT: t('e.g. 50%'),
  };

  return (
    <form.Field
      name="collectCommentsMode"
      children={(modeField: any) => {
        const mode: CollectCommentsMode = modeField.state.value || 'ALL';
        const activeFieldName = mode === 'ALL' ? null : VALUE_FIELDS[mode].name;

        return (
          <div className={styles.root}>
            <span id={LABEL_ID} className={styles.title}>
              {t('Comments per post')}
            </span>

            <div className={styles.controls}>
              <SegmentedControl
                options={modeOptions}
                value={mode}
                onChange={(value) => modeField.handleChange(value)}
                aria-labelledby={LABEL_ID}
                disabled={isDisabled}
              />

              {(Object.keys(VALUE_FIELDS) as ValueMode[]).map((valueMode) => {
                const { name, max } = VALUE_FIELDS[valueMode];

                return (
                  <form.Field
                    key={name}
                    name={name}
                    children={(field: any) => {
                      // The input is removed from the layout (not disabled) in
                      // other modes; the form.Field stays mounted so the value survives.
                      if (mode !== valueMode) return null;

                      const hasError = field.state.meta.errors?.length > 0;

                      return (
                        <input
                          id={name}
                          name={name}
                          type="number"
                          inputMode="numeric"
                          min={COLLECT_COMMENTS_MIN_VALUE}
                          max={max}
                          step={1}
                          className={classNames(styles.input, { [styles.inputError]: hasError })}
                          value={field.state.value ?? ''}
                          placeholder={placeholders[valueMode]}
                          aria-labelledby={LABEL_ID}
                          aria-describedby={hasError ? `${HINT_ID} ${ERROR_ID}` : HINT_ID}
                          aria-invalid={hasError || undefined}
                          disabled={isDisabled}
                          onChange={(event) =>
                            field.handleChange(event.target.value === '' ? null : Number(event.target.value))
                          }
                          onBlur={() => {
                            const current = field.state.value;

                            if (typeof current === 'number' && !Number.isNaN(current)) {
                              // 0 (or anything out of range) silently clamps to the range on blur.
                              const clamped = clampToRange(current, COLLECT_COMMENTS_MIN_VALUE, max);
                              if (clamped !== current) field.handleChange(clamped);

                              form.setFieldMeta(name, (prev: any) => ({
                                ...prev,
                                errorMap: { ...prev.errorMap, onSubmit: undefined },
                              }));
                            }

                            field.handleBlur();
                          }}
                        />
                      );
                    }}
                  />
                );
              })}
            </div>

            <p id={HINT_ID} className={styles.hint}>
              {hints[mode]}
            </p>

            <form.Subscribe
              selector={(state: any) =>
                activeFieldName ? state.fieldMeta[activeFieldName]?.errors?.[0] : undefined
              }
              children={(error: unknown) =>
                typeof error === 'string' ? (
                  <p id={ERROR_ID} className={styles.error}>
                    {t(error)}
                  </p>
                ) : null
              }
            />
          </div>
        );
      }}
    />
  );
};
